import React from 'react';
import { encode, decode } from 'js-base64';

export const LS_KEYS = {
  BACKOFFICE: '@MyFoodBackoffice',
  SITE: '@MyFoodSite',
};

export const useLocalStorage = (lsKey = LS_KEYS.BACKOFFICE) => {
  const [lsValues, setLsValues] = React.useState(null);

  const loadLSValues = React.useCallback(() => {
    const lsValueEncoded = window.localStorage.getItem(lsKey);
    const values = lsValueEncoded ? JSON.parse(decode(lsValueEncoded)) : null;
    return values;
  }, [lsKey]);

  const setLSItem = React.useCallback(
    (key, value) => {
      setLsValues(state => {
        const newValue = !state
          ? { [key]: JSON.stringify(value) }
          : { ...state, [key]: JSON.stringify(value) };

        window.localStorage.setItem(lsKey, encode(JSON.stringify(newValue)));
        setLsValues(newValue);
        return newValue;
      });
    },
    [lsKey],
  );

  const getLSItem = React.useCallback(
    key => {
      const values = loadLSValues();
      return values ? JSON.parse(values[key] || null) : null;
    },
    [loadLSValues],
  );

  const removeLSItem = React.useCallback(
    key => {
      setLsValues(state => {
        if (!state) return null;

        const { [key]: removed, ...newValue } = state;

        if (!Object.keys(newValue).length) {
          window.localStorage.removeItem(lsKey);
          setLsValues(null);
          return null;
        }
        window.localStorage.setItem(lsKey, encode(JSON.stringify(newValue)));
        setLsValues(newValue);
        return newValue;
      });
    },
    [lsKey],

    React.useEffect(() => {
      setLsValues(loadLSValues());
    }, [loadLSValues]),
  );

  return { setLSItem, getLSItem, removeLSItem, lsValues };
};

export default useLocalStorage;
